/**
 * The day's work, as a list of questions the book can answer.
 *
 * ## The rule this file exists to enforce
 *
 * **A count and the rows it opens must be the same question.** A card saying
 * "12 overdue" that opens a list of nine is worse than no card: the person
 * stops trusting the screen, and they are right to.
 *
 * The way that is guaranteed here is not care — it is structure. Each group
 * below carries the *book filter* that defines it, and two things use that one
 * value: the link the card opens, and the assertion that the card's count
 * equals `book_totals()` under that filter. There is no second definition of
 * "overdue" anywhere for the two to drift apart from.
 *
 * ## What is not here
 *
 * No counting. The figures come from `worklist_summary()` in SQL, over the
 * caller's whole visible book — not from rows loaded into a browser. A morning
 * screen that counted the first page would tell a collector with 1,800
 * accounts that they have nothing to do.
 *
 * The reference implementation's `worklistSummary()` and `attentionCounts()`
 * take an array of customers and are still the right shape for a small,
 * already-loaded list. They are not the right shape for this screen, and using
 * them here would have quietly reintroduced page-sized truth.
 */
import type { BookFilters } from './book.js';
import { DEFAULT_FILTERS } from './book.js';

/** The groups a day is divided into, in the order somebody works them. */
export type WorklistGroup =
    | 'overdue'
    | 'today'
    | 'urgent'
    | 'no_follow_up'
    | 'future'
    | 'bad_debt';

export interface WorklistGroupDefinition {
    group: WorklistGroup;
    label: string;
    /** One line saying what the group means, shown under the count. */
    hint: string;
    /** What an empty group means. Never "0 results". */
    empty: string;
    tone: 'dang' | 'warn' | 'brand' | 'neutral' | 'pos';
    /**
     * The book filter this group *is*.
     *
     * The card links here and the count is asserted against `book_totals()`
     * with exactly these values. Changing the meaning of a group means changing
     * this one line, and both follow.
     */
    filters: Partial<BookFilters>;
}

/**
 * Every group, defined once.
 *
 * `bad_debt` is deliberately a group rather than an exclusion. The reference
 * implementation left declared defaulters out of the worklist boxes and
 * counted them separately, which is right for the *work* — chasing a defaulter
 * is a decision, not a daily reflex — but it also meant the boxes and the book
 * disagreed about what "overdue" contained. Here every group is exactly a book
 * filter, defaulters included where the filter includes them, and the
 * defaulters card sits beside the rest as its own question.
 */
/**
 * The queue is the accounts not marked as bad debt (PRODUCT-UX-FOUNDATION
 * §7): a declared defaulter is recovery, not routine, and sits behind its own
 * link. Every queue group carries it, so the count and its list agree
 * (`worklist_summary()`, 0033).
 */
const IN_QUEUE = { rank: 'Good,Late' } as const;

export const WORKLIST_GROUPS: readonly WorklistGroupDefinition[] = [
    {
        // The follow-up is late. "Overdue" is money past its due date, and this is not that (semantics.ts).
        group: 'overdue',
        label: 'Late',
        hint: 'Their follow-up date has gone by',
        empty: 'Nothing has been left behind.',
        tone: 'warn',
        filters: { followUp: 'overdue', ...IN_QUEUE },
    },
    {
        group: 'today',
        label: 'Due today',
        hint: 'You said you would call them today',
        empty: 'Nothing is due today.',
        tone: 'warn',
        filters: { followUp: 'today', ...IN_QUEUE },
    },
    {
        group: 'urgent',
        label: 'Marked urgent',
        hint: 'Somebody flagged these for attention',
        empty: 'Nothing is flagged.',
        tone: 'dang',
        filters: { urgentOnly: true, ...IN_QUEUE },
    },
    {
        group: 'no_follow_up',
        label: 'No follow-up set',
        hint: 'Money owed and nothing planned',
        empty: 'Every account that owes has a date.',
        tone: 'neutral',
        filters: { followUp: 'no_follow_up', ...IN_QUEUE },
    },
    {
        group: 'future',
        label: 'Scheduled',
        hint: 'Planned for a day still to come',
        empty: 'Nothing is booked ahead.',
        tone: 'brand',
        filters: { followUp: 'future', ...IN_QUEUE },
    },
    {
        group: 'bad_debt',
        label: 'Bad debt',
        hint: 'Marked by a person — still outstanding; recovery, not routine work',
        empty: 'No account is marked as bad debt.',
        tone: 'neutral',
        filters: { rank: 'Bad' },
    },
];

export const worklistGroup = (group: WorklistGroup): WorklistGroupDefinition =>
    WORKLIST_GROUPS.find(g => g.group === group)!;

/**
 * The address a group's card opens.
 *
 * The book's own defaults are left out of the query string, exactly as the
 * book leaves them out when somebody changes a filter by hand — so the link a
 * card produces is the link the book would have produced, and a back press
 * from an account returns to the same list.
 */
export function worklistQuery(group: WorklistGroup): string {
    const definition = worklistGroup(group);
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(definition.filters)) {
        if (value === undefined || value === '' || value === false) continue;
        if (key === 'settlement' && value === DEFAULT_FILTERS.settlement) continue;
        if (value === 'any') continue;
        params.set(key, String(value));
    }

    return params.toString();
}

/* ------------------------------ the figures ------------------------------- */

/** One group's count and amount, as `worklist_summary()` returns them. */
export interface WorklistFigure {
    count: number;
    amount: number;
}

/**
 * The whole of what the morning screen shows, from the database.
 *
 * Amounts are netted, not summed: a credit is money being held for the
 * customer, and adding its magnitude to a debit overstates the day twice over.
 */
export interface WorklistTotals {
    groups: Record<WorklistGroup, WorklistFigure>;
    /** Every account with dues, defaulters included. */
    total: WorklistFigure;
    /** What customers have committed to, and by when. */
    promisedToday: WorklistFigure;
    promisedWeek: WorklistFigure;
    promisedOpen: WorklistFigure;
}

const figure = (count: unknown, amount: unknown): WorklistFigure => ({
    count: Number(count) || 0,
    amount: Number(amount) || 0,
});

/** The row `worklist_summary()` returns, turned into the shape a screen wants. */
export function readWorklistSummary(row: Record<string, unknown> | null | undefined): WorklistTotals {
    const r = row ?? {};
    return {
        groups: {
            overdue: figure(r['overdue_count'], r['overdue_amount']),
            today: figure(r['today_count'], r['today_amount']),
            urgent: figure(r['urgent_count'], r['urgent_amount']),
            no_follow_up: figure(r['no_follow_up_count'], r['no_follow_up_amount']),
            future: figure(r['future_count'], r['future_amount']),
            bad_debt: figure(r['bad_debt_count'], r['bad_debt_amount']),
        },
        total: figure(r['total_count'], r['total_amount']),
        promisedToday: figure(r['promised_today_count'], r['promised_today_amount']),
        promisedWeek: figure(r['promised_week_count'], r['promised_week_amount']),
        promisedOpen: figure(r['promised_count'], r['promised_amount']),
    };
}

/**
 * Whether there is anything to do at all.
 *
 * Deliberately not "every group is zero": an account with money owed and no
 * date set is work even though nobody has scheduled it, and a day with only
 * scheduled-for-later accounts genuinely is a clear day.
 */
export const dayIsClear = (totals: WorklistTotals): boolean =>
    totals.groups.overdue.count === 0
    && totals.groups.today.count === 0
    && totals.groups.urgent.count === 0
    && totals.groups.no_follow_up.count === 0;

/* ------------------------------ coverage ---------------------------------- */

/**
 * How much of a book has a plan: of the accounts that owe and are not written
 * off, how many have a next follow-up that has not passed, or are marked
 * collected.
 *
 * A proportion of two counts the database made — never a score, never a
 * prediction. The rest are the overdue and the not-yet-dated accounts, which
 * is exactly what the figure's link opens (`followUp=overdue,no_follow_up`),
 * so every part of it is a door to the accounts it counted.
 */
export interface Coverage {
    planned: number;
    total: number;
    /** Between 0 and 1; null when there is nothing to plan. */
    share: number | null;
}

export function followUpCoverage(figures: {
    total: number;
    overdue: number;
    noFollowUp: number;
    /** Accounts marked as bad debt in `total`: outside the queue, so outside the plan. */
    writtenOff?: number;
}): Coverage {
    const total = Math.max(0, figures.total - Math.max(0, figures.writtenOff ?? 0));
    const planned = Math.max(0, total - Math.max(0, figures.overdue) - Math.max(0, figures.noFollowUp));
    return { planned, total, share: total > 0 ? planned / total : null };
}

/** The list behind the part of a book that has no plan: overdue or not set, not marked as bad debt. */
export const UNPLANNED_QUERY = 'followUp=overdue%2Cno_follow_up&rank=Good%2CLate';
